import { cn, PageHeader } from '@repo/ui'
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from '@tanstack/react-router'
import {
  Building2Icon,
  CheckCircle2Icon,
  ChevronRightIcon,
  CreditCardIcon,
  FileTextIcon,
  GaugeIcon,
  ImagesIcon,
  LayersIcon,
  MessageSquareIcon,
  SettingsIcon,
  ShieldAlertIcon,
  SmilePlusIcon,
  TagIcon,
  UsersIcon,
} from 'lucide-react'

export const Route = createFileRoute('/_app/organizacao')({
  component: OrganizacaoLayout,
})

interface TabConfig {
  to: string
  label: string
  description: string
  icon: React.ElementType
}

const TABS: TabConfig[] = [
  {
    to: '/organizacao',
    label: 'Geral',
    description: 'Nome, logo e perfil da organização',
    icon: Building2Icon,
  },
  {
    to: '/organizacao/assinatura',
    label: 'Assinatura',
    description: 'Plano atual, limites e cobrança',
    icon: CreditCardIcon,
  },
  {
    to: '/organizacao/membros',
    label: 'Membros',
    description: 'Gerencie usuários e permissões',
    icon: UsersIcon,
  },
  {
    to: '/organizacao/setores',
    label: 'Setores',
    description: 'Equipes e departamentos de atendimento',
    icon: LayersIcon,
  },
  {
    to: '/organizacao/tags',
    label: 'Tags',
    description: 'Etiquetas para contatos e atendimentos',
    icon: TagIcon,
  },
  {
    to: '/organizacao/campos',
    label: 'Campos',
    description: 'Campos personalizados de contato',
    icon: SettingsIcon,
  },
  {
    to: '/organizacao/respostas-rapidas',
    label: 'Respostas rápidas',
    description: 'Atalhos de mensagem para o atendimento',
    icon: MessageSquareIcon,
  },
  {
    to: '/organizacao/templates',
    label: 'Templates',
    description: 'Modelos de mensagem do canal oficial (Meta)',
    icon: FileTextIcon,
  },
  {
    to: '/organizacao/midias',
    label: 'Mídias',
    description: 'Biblioteca de arquivos e imagens reutilizáveis',
    icon: ImagesIcon,
  },
  {
    to: '/organizacao/mensagens',
    label: 'Mensagens',
    description: 'Velocidade de envio e tempo de resposta da IA',
    icon: GaugeIcon,
  },
  {
    to: '/organizacao/nps',
    label: 'Pesquisa NPS',
    description: 'Pesquisa de satisfação automática pós-atendimento',
    icon: SmilePlusIcon,
  },
  {
    to: '/organizacao/motivos-finalizacao',
    label: 'Motivos de finalização',
    description: 'Razões para encerrar um atendimento',
    icon: CheckCircle2Icon,
  },
  {
    to: '/organizacao/privacidade',
    label: 'Privacidade',
    description: 'Solicitações LGPD: exportação, anonimização e exclusão',
    icon: ShieldAlertIcon,
  },
]

function OrganizacaoLayout() {
  const { pathname } = useLocation()

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <PageHeader
        icon={Building2Icon}
        title="Organização"
        description="Configure sua organização, membros, setores e personalização"
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TABS.map((tab) => {
            const Icon = tab.icon
            const isActive =
              tab.to === '/organizacao'
                ? pathname === '/organizacao'
                : pathname.startsWith(tab.to)
            return (
              <Link
                key={tab.to}
                to={tab.to}
                className={cn(
                  'bg-card flex items-center gap-4 rounded-xl border p-4 text-left transition-all',
                  isActive
                    ? 'border-primary ring-primary/20 ring-2'
                    : 'hover:border-primary/30'
                )}
              >
                <div
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-primary/10 text-primary'
                  )}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{tab.label}</p>
                  <p className="text-muted-foreground text-xs">
                    {tab.description}
                  </p>
                </div>
                <ChevronRightIcon
                  className={cn(
                    'text-muted-foreground size-4 shrink-0 transition-transform',
                    isActive && 'rotate-90'
                  )}
                />
              </Link>
            )
          })}
        </div>

        <div className="mt-6">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
